import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback,
  memo,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  IconButton,
  IconMoreVert,
  IconChevronRight,
  SimpleTable,
  SimpleTableBody,
  SimpleTableData,
  SimpleTableHead,
  SimpleTableHeader,
  SimpleTableRow,
  SimpleTableWrapper,
  SimpleTableContainer,
  IconStatusWarningExclamation,
  Menu,
  MenuListItem,
  TextButton,
  IconEdit,
  IconVisibility,
  IconCheck,
  IconRemove,
  IconAdd,
  IconDelete,
  StatusIndicator,
  STATUS_INDICATOR_VARIANTS,
  IconStatusDefault,
  withTooltip,
  TOOLTIP_HORIZONTAL_ALIGNMENTS,
  TOOLTIP_VERTICAL_ALIGNMENTS,
  THEMES,
  IconSales,
} from 'cdk-radial';
import WebhookWarningDialog from './WebhookWarningDialog';
import classNames from 'classnames';
import { groupBy } from 'lodash';

import './APIIntegrationsTableV2.scss';
import {
  API_TYPES,
  API_TYPES_OPTIONS,
  DUMMY_WEBHOOK_URL,
} from '../../../../../constants/APIDirectoryConstants';
import {
  getAPIDetailsById,
  setAPIDirectoryPage,
} from '../../../../../redux/connectedComponent/apiDirectory/apiDirectory.slice';
import { APITerms } from './APITerms';
import { APIWebhook } from './APIWebhook';
import {
  storeActiveApiList,
} from '../../../../../redux/connectedComponent/orderAPI/orderAPI.slice';
import {
  getMatchingResources,
  hasIntegrationCertified,
} from '../../../../../utils/utils';
import { clearSubscriptions } from '../../../../../redux/connectedComponent/daabSubscriptions/daabSubscriptions.slice';
import { APIPricingPlan } from './APIPricingPlan';
import { setScreen } from '../../../../../redux/connectedComponent/uiFactory/uiFactory.slice';
import { screens } from '../../../../config/uiFactory.config';
import { NoDataOverlay } from '../../../utils/NoDataOverlay';
import { PAGE_TITLE, RESOURCE_TYPES } from '../../../../../constants/constants';
import { APIDelete } from './APIDelete';
import { APIDeleteBundle } from './APIDeleteBundle';
import {
  getOrgAmplitudeProperties,
  sendAmplitudeData,
} from '../../../../../utils/amplitude';
import { trackManageApiIntegration, getFormattedApiType } from '../../../../../utils/gtm';
import { AMPLITUDE_EVENTS } from '../../../../../constants/amplitudeConstants';
import { bundleSelectedLocations } from '../../../../../redux/connectedComponent/orderAPI/orderAPI.slice';
const NA = 'N/A';
const WEBHOOK_BUTTON_TEXT = 'Webhook';
const API_DIR_URL = '/dp/api-directory';

const ACTION_TYPES = Object.freeze({
  ADD_LOCATIONS: 'ADD_LOCATIONS',
  REMOVE_LOCATIONS: 'REMOVE_LOCATIONS',
  EDIT_PLAN: 'EDIT_PLAN',
  SEE_TERMS: 'SEE_TERMS',
  DELETE_API: 'DELETE_API',
  MANAGE_LOCATIONS: 'MANAGE_LOCATIONS',
});

const MENU_OPTIONS = Object.freeze({
  bundle: [
    {
      label: 'Manage Locations',
      action: ACTION_TYPES.MANAGE_LOCATIONS,
      icon: IconCheck,
    },
    {
      label: 'Edit Pricing Plan',
      action: ACTION_TYPES.EDIT_PLAN,
      icon: IconEdit,
    },
    {
      label: 'View Terms',
      action: ACTION_TYPES.SEE_TERMS,
      icon: IconVisibility,
    },
    { label: 'Delete', action: ACTION_TYPES.DELETE_API, icon: IconDelete },
  ],
  bundleContracted: [
    {
      label: 'Manage Locations',
      action: ACTION_TYPES.MANAGE_LOCATIONS,
      icon: IconCheck,
    },
  ],
  bundleChild: [
    {
      label: 'Add Locations',
      action: ACTION_TYPES.ADD_LOCATIONS,
      icon: IconAdd,
    },
    {
      label: 'Remove Locations',
      action: ACTION_TYPES.REMOVE_LOCATIONS,
      icon: IconRemove,
    },
  ],
  contracted: [
    {
      label: 'Add Locations',
      action: ACTION_TYPES.ADD_LOCATIONS,
      icon: IconAdd,
    },
    {
      label: 'Remove Locations',
      action: ACTION_TYPES.REMOVE_LOCATIONS,
      icon: IconRemove,
    },
  ],
  standard: [
    {
      label: 'Add Locations',
      action: ACTION_TYPES.ADD_LOCATIONS,
      icon: IconAdd,
    },
    {
      label: 'Remove Locations',
      action: ACTION_TYPES.REMOVE_LOCATIONS,
      icon: IconRemove,
    },
    {
      label: 'Edit Pricing Plan',
      action: ACTION_TYPES.EDIT_PLAN,
      icon: IconEdit,
    },
    {
      label: 'View Terms',
      action: ACTION_TYPES.SEE_TERMS,
      icon: IconVisibility,
    },
    { label: 'Delete', action: ACTION_TYPES.DELETE_API, icon: IconDelete },
  ],
});

const createStyledComponent = (Component, baseClassName) => {
  const StyledComponent = memo(({ className, ...props }) => (
    <Component className={classNames(baseClassName, className)} {...props} />
  ));
  StyledComponent.displayName = `Styled${
    Component.displayName || Component.name || 'Component'
  }`;
  return StyledComponent;
};

const StyledIconStatusWarningExclamation = createStyledComponent(
  IconStatusWarningExclamation,
  'styled-icon-status-warning'
);
const StyledIconStatusDefault = createStyledComponent(
  IconStatusDefault,
  'styled-bundle-new-api-icon'
);
const StyledSimpleTableWrapper = createStyledComponent(
  SimpleTableWrapper,
  'styled-simple-table-wrapper'
);
const StyledSimpleTableContainer = createStyledComponent(
  SimpleTableContainer,
  'styled-simple-table-container'
);
const StyledSimpleTable = createStyledComponent(
  SimpleTable,
  'styled-simple-table'
);
const StyledSimpleTableHead = createStyledComponent(
  SimpleTableHead,
  'styled-simple-table-head'
);
const StyledSimpleTableBody = createStyledComponent(
  SimpleTableBody,
  'styled-simple-table-body'
);
const StyledSimpleTableData = createStyledComponent(
  SimpleTableData,
  'styled-simple-table-data'
);
const StyledSimpleTableRow = createStyledComponent(
  SimpleTableRow,
  'styled-simple-table-row'
);
const StickyHeaderRow = createStyledComponent(
  SimpleTableRow,
  'sticky-header-row'
);
const StickySimpleTableHeader = createStyledComponent(
  SimpleTableHeader,
  'sticky-simple-table-header'
);

const StyledIconButton = memo(({ isOpen, className, ...props }) => (
  <IconButton
    className={classNames(
      'styled-icon-button',
      { 'is-open': isOpen },
      className
    )}
    {...props}
  />
));
StyledIconButton.displayName = 'StyledIconButton';

const ActionMenuWrapper = memo(({ className, children, ...props }) => (
  <div className={classNames('action-menu-wrapper', className)} {...props}>
    {children}
  </div>
));
ActionMenuWrapper.displayName = 'ActionMenuWrapper';

const ContractedIconWithTooltip = withTooltip(
  (props) => (
    <IconSales {...props} className="api-integrations-contracted-apis" />
  ),
  {
    id: 'contract-icon',
    text: 'Contracted Off Platform',
    theme: THEMES.LIGHT,
  }
);

const CONTRACTED_ICON_TOOLTIP_PROPS = {
  horizontalAlignment: TOOLTIP_HORIZONTAL_ALIGNMENTS.MIDDLE,
  marginAroundElement: 2,
  verticalAlignment: TOOLTIP_VERTICAL_ALIGNMENTS.MIDDLE,
};

const getWebhookFromIntegrations = (integrations) => {
  if (!Array.isArray(integrations) || integrations.length === 0) return null;
  return integrations[0]?.webhookUrl || null;
};

const createApiDataObject = (apiData, consentsByApiId) => {
  const webhook = getWebhookFromIntegrations(apiData.integrations);
  const consentKey =
    apiData.type === 'api' ? `api-v2-${apiData.id}` : apiData.id;

  return {
    name: apiData.name,
    id: apiData.id,
    orgId: apiData.orgId,
    type: apiData.type,
    webhookUrl: webhook,
    integrations: apiData.integrations,
    pricingPlans: apiData.pricingPlans,
    consent: consentsByApiId?.[consentKey]?.[0] || {},
  };
};

const sortByName = (items) => {
  return [...items].sort((a, b) => {
    const nameA = (a?.name || '').toLowerCase().trim();
    const nameB = (b?.name || '').toLowerCase().trim();
    return nameA.localeCompare(nameB);
  });
};

const getBundleTypeLabel = (bundleApis = []) => {
  if (!Array.isArray(bundleApis) || bundleApis.length === 0) return '-';

  const hasRest = bundleApis.some(
    (bundleApi) => bundleApi?.apiType === API_TYPES.REST,
  );
  const hasAsync = bundleApis.some(
    (bundleApi) => bundleApi?.apiType === API_TYPES.ASYNC,
  );

  if (hasRest && hasAsync) {
    return `${API_TYPES_OPTIONS.REST} and ${API_TYPES_OPTIONS.ASYNC}`;
  }

  if (hasRest) return API_TYPES_OPTIONS.REST;
  if (hasAsync) return API_TYPES_OPTIONS.ASYNC;

  return '-';
};

const isSameSerializedData = (prev, next) =>
  JSON.stringify(prev) === JSON.stringify(next);

const getMenuOptions = (isBundle, isPartOfBundle, isContracted) => {
  if (isBundle && isContracted) return MENU_OPTIONS.bundleContracted;
  if (isBundle) return MENU_OPTIONS.bundle;
  if (isPartOfBundle) return MENU_OPTIONS.bundleChild;
  if (isContracted) return MENU_OPTIONS.contracted;
  return MENU_OPTIONS.standard;
};

const useAppState = () => {
  const token = useSelector(
    (state) => state?.loginDetailsSlice?.authenticationToken
  );
  const activeApiList = useSelector(
    (state) => state?.orderAPISlice?.activeApiList
  );
  const orders = useSelector((state) => state?.orderAPISlice?.orders);
  const orderUpdated = useSelector(
    (state) => state?.orderAPISlice?.orderUpdated
  );
  const errorUpdatingOrder = useSelector(
    (state) => state?.orderAPISlice?.errorUpdatingOrder
  );
  const consents = useSelector((state) => state?.orderAPISlice?.consents);
  const bundleConsents = useSelector(
    (state) => state?.orderAPISlice?.bundleConsents
  );
  const subscriptionCounts = useSelector(
    (state) => state?.orderAPISlice?.subscriptionCounts
  );
  const loginDetailsSlice = useSelector((state) => state?.loginDetailsSlice);

  const apisObject = useMemo(
    () =>
      (orders?.restApis || []).reduce((acc, api) => {
        acc[api.id] = api;
        return acc;
      }, {}),
    [orders?.restApis]
  );

  const asyncApisObject = useMemo(
    () =>
      (orders?.asyncApis || []).reduce((acc, api) => {
        acc[api.id] = api;
        return acc;
      }, {}),
    [orders?.asyncApis]
  );

  const bundlesObject = useMemo(
    () =>
      (orders?.bundleIntegrations || []).reduce((acc, b) => {
        acc[b.id] = { kind: 'bundle', ...b };
        return acc;
      }, {}),
    [orders?.bundleIntegrations]
  );

  const bundles = useMemo(
    () => orders?.bundleIntegrations || [],
    [orders?.bundleIntegrations]
  );

  return useMemo(
    () => ({
      token,
      activeApiList,
      apisObject,
      asyncApisObject,
      bundlesObject,
      bundles,
      orders,
      orderUpdated,
      errorUpdatingOrder,
      consents,
      bundleConsents,
      subscriptionCounts,
      orgAmplitudeProperties: getOrgAmplitudeProperties(loginDetailsSlice),
    }),
    [
      token,
      activeApiList,
      apisObject,
      asyncApisObject,
      bundlesObject,
      bundles,
      orders,
      orderUpdated,
      errorUpdatingOrder,
      consents,
      bundleConsents,
      subscriptionCounts,
      loginDetailsSlice,
    ]
  );
};

const useScrollToTop = (dependencies) => {
  const tableBodyRef = useRef(null);

  useLayoutEffect(() => {
    const scrollToTop = () => {
      if (tableBodyRef.current) {
        tableBodyRef.current.scrollTop = 0;
      }

      const selectors = [
        '[data-testid="api-table-body"]',
        '#api-integration-table tbody',
      ];
      selectors.forEach((selector) => {
        const element = document.querySelector(selector);
        if (element) element.scrollTop = 0;
      });
    };

    scrollToTop();
    requestAnimationFrame(scrollToTop);
    const timeoutId = setTimeout(scrollToTop, 200);
    return () => clearTimeout(timeoutId);
  }, dependencies);

  return tableBodyRef;
};

const WebhookRender = memo(({ data, isLoadingTableData }) => {
  const [showWebhook, setShowWebhook] = useState(false);
  const { type, webhookUrl } = data?.data || {};

  const handleViewWebhook = useCallback(() => setShowWebhook(true), []);
  const handleClose = useCallback(() => setShowWebhook(false), []);

  if (type !== API_TYPES.ASYNC) {
    return <TextButton isDisabled text={NA} />;
  }

  const hasValidWebhook = webhookUrl && webhookUrl !== DUMMY_WEBHOOK_URL;

  return (
    <>
      {hasValidWebhook ? (
        <TextButton
          className="link-renderer text-wrap"
          onClick={handleViewWebhook}
          isDisabled={isLoadingTableData}
          text={webhookUrl}
        />
      ) : (
        <TextButton
          className="add-webhook-button"
          icon={<StyledIconStatusWarningExclamation />}
          text={WEBHOOK_BUTTON_TEXT}
          onClick={handleViewWebhook}
          isDisabled={isLoadingTableData}
        />
      )}
      <APIWebhook
        data={data?.data}
        isOpen={showWebhook}
        handleClose={handleClose}
      />
    </>
  );
});
WebhookRender.displayName = 'WebhookRender';

const APINameRender = memo(
  ({ data, isLoadingTableData, isPartOfBundle = false }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { token, apisObject, asyncApisObject, bundlesObject } = useAppState();
    const [isLoadingAPIDetails, setIsLoadingAPIDetails] = useState(false);

    const api = useMemo(
      () =>
        getMatchingResources(
          { apiType: data?.type, apiId: data?.id },
          apisObject,
          asyncApisObject,
          bundlesObject
        ),
      [data?.type, data?.id, apisObject, asyncApisObject, bundlesObject]
    );

    const handleViewAPIDetails = useCallback(
      async (apiData) => {
        const { id: apiId, type: apiType } = apiData;
        const location = { search: 'q=Specification' };

        setIsLoadingAPIDetails(true);
        try {
          const response = await dispatch(
            getAPIDetailsById({ apiId, apiType, token })
          ).unwrap();

          if (!response || (typeof response === 'object' && Object.keys(response).length === 0)) {
            console.error('API returned no data!');
            setIsLoadingAPIDetails(false);
            return;
          }
          dispatch(setAPIDirectoryPage({ page: 'apiDetails', props: { apiId, location, prevScreen: 'myApis' } }));
          history.push(API_DIR_URL);
          window.location.assign(API_DIR_URL);
          setIsLoadingAPIDetails(false);
        } catch (error) {
          console.error('API call failed:', error);
          setIsLoadingAPIDetails(false);
        }
      },
      [dispatch, token, history]
    );

    return (
      <>
        <div className="api-name-content">
          {isPartOfBundle && (
            <div className="new-api-status-indicator">
              {data?.isNewApi && (
                <StatusIndicator
                  className="new-api-indicator"
                  label="New"
                  backgroundColor="#DAF2D9"
                  borderColor="#09AD00"
                  variant={STATUS_INDICATOR_VARIANTS.CUSTOM}
                />
              )}

            </div>
          )}
        <TextButton
          className={classNames('link-renderer text-wrap', {
            'api-details-loading': isLoadingAPIDetails,
          })}
          text={data?.name}
          onClick={() => handleViewAPIDetails(data)}
          isDisabled={isLoadingTableData || isLoadingAPIDetails}
        />
        {api?.isContracted && !isPartOfBundle && (
          <ContractedIconWithTooltip {...CONTRACTED_ICON_TOOLTIP_PROPS} />
        )}
        </div>
      </>
    );
  }
);
APINameRender.displayName = 'APINameRender';

const LocationsRender = memo(({
  data,
  isLoadingTableData,
  isPartOfBundle = false,
  bundleId,
  bundleLocationsData,
  subscriptionCount,
}) => {
  const dispatch = useDispatch();
  const { orgAmplitudeProperties } = useAppState();
  const { id, type, webhookUrl, name, orgId } = data;

  const handleClick = useCallback(() => {
    dispatch(clearSubscriptions());
    dispatch(
      setScreen({
        screen: screens.VIEW_LOCATIONS,
        additionalData: {
          id,
          type,
          webhookUrl,
          name,
          orgId,
          bundleId: isPartOfBundle ? bundleId : undefined,
          bundleLocations: isPartOfBundle ? bundleLocationsData : undefined,
        },
      })
    );
    sendAmplitudeData(
      AMPLITUDE_EVENTS.MY_APIS_VIEW_LOCATIONS,
      orgAmplitudeProperties
    );
  }, [
    dispatch,
    id,
    type,
    webhookUrl,
    name,
    orgId,
    isPartOfBundle,
    bundleId,
    bundleLocationsData,
    orgAmplitudeProperties,
  ]);

  const countDisplay = subscriptionCount !== undefined && subscriptionCount !== null 
    ? ` (${subscriptionCount})` 
    : '';

  return (
    <TextButton
      data-testid={`manage-location-${id}`}
      className="link-renderer text-wrap"
      text={`View Locations${countDisplay}`}
      onClick={handleClick}
      isDisabled={isLoadingTableData}
    />
  );
});
LocationsRender.displayName = 'LocationsRender';

const ApiTypeButton = memo(({ type, isLoadingTableData }) => {
  const typeConfig = {
    [API_TYPES.REST]: { testId: 'api-type-rest', text: API_TYPES_OPTIONS.REST },
    [API_TYPES.ASYNC]: {
      testId: 'api-type-async',
      text: API_TYPES_OPTIONS.ASYNC,
    },
  };

  const config = typeConfig[type];
  if (!config) return null;

  return (
    <TextButton
      className={classNames('text-renderer text-wrap', {
        'api-type-button': !isLoadingTableData,
      })}
      dataTestId={config.testId}
      text={config.text}
      isDisabled={isLoadingTableData}
    />
  );
});
ApiTypeButton.displayName = 'ApiTypeButton';

const ActionMenuRenderer = memo(
  ({
    gridParams,
    setShowEditPlan,
    setSelectedAPIEditPlanContext,
    handleAddLocation,
    handleRemoveLocation,
    handleBundleEnableLocations, // ADD THIS PROP
    isFetchingBundleLocations = false,
    isLoadingTableData,
    isPartOfBundle = false,
    isBundle = false,
    onApiDeleted,
    bundleId,
    bundleLocationsData,
    hasPendingReviewApi = false,
    bundleIntegration = null,
  }) => {
    const dispatch = useDispatch();
    const {
      token,
      activeApiList,
      apisObject,
      asyncApisObject,
      bundlesObject,
      orgAmplitudeProperties,
    } = useAppState();

    const [isOpen, setIsOpen] = useState(false);
    const [viewApiTerms, setViewApiTerms] = useState(false);
    const [viewDeleteApiDialog, setViewDeleteApiDialog] = useState(false);
    const [viewDeleteBundleDialog, setViewDeleteBundleDialog] = useState(false);
    const targetRef = useRef();

    const api = useMemo(
      () => {
      
      const result = getMatchingResources(
        { apiType: gridParams?.data?.type || RESOURCE_TYPES.BUNDLE , apiId: gridParams?.data?.id || gridParams?.data?.bundle?.id},
        apisObject,
        asyncApisObject,
        bundlesObject
      );
      
      return result;
      },
      [
      gridParams?.data,
      apisObject,
      asyncApisObject,
      bundlesObject,
      ]
    );

    const options = useMemo(() => {
      const bundleData = gridParams?.data?.bundle;
      const isBundleContracted = isBundle && Boolean(bundleData?.isContracted);

      const menuOptions = getMenuOptions(
        isBundle,
        isPartOfBundle,
        isBundleContracted || api?.isContracted
      );
      if (
        isPartOfBundle &&
        (!bundleLocationsData || bundleLocationsData.length === 0)
      ) {
        return menuOptions
          .filter((opt) => opt.action !== ACTION_TYPES.ADD_LOCATIONS)
          .map((opt) => ({
            ...opt,
            icon: <opt.icon />,
          }));
      }
      return menuOptions.map((opt) => ({
        ...opt,
        icon: <opt.icon />,
      }));
    }, [
      isBundle,
      isPartOfBundle,
      api?.isContracted,
      bundleLocationsData,
      gridParams?.data?.bundle,
    ]);

    const handleClose = useCallback(() => {
      setIsOpen(false);
      targetRef.current?.focus();
    }, []);

    const handleOpen = useCallback(() => setIsOpen(true), []);

    const handleCloseApiTerms = useCallback(() => {
      setViewApiTerms(false);
      targetRef.current?.focus();
    }, []);

    const handleCloseDeleteApiDialog = useCallback(
      (wasDeleted = false) => {
        setViewDeleteApiDialog(false);
        if (wasDeleted && onApiDeleted) {
          onApiDeleted(gridParams?.data?.id, gridParams?.data?.type);
        }
        sendAmplitudeData(
          AMPLITUDE_EVENTS.DELETE_API_CONFIRMATION_CANCEL,
          orgAmplitudeProperties
        );
        targetRef.current?.focus();
      },
      [
        orgAmplitudeProperties,
        onApiDeleted,
        gridParams?.data?.id,
        gridParams?.data?.type,
      ]
    );

    const handleCloseDeleteBundleDialog = useCallback(
      (wasDeleted = false) => {
        setViewDeleteBundleDialog(false);
        if (wasDeleted && onApiDeleted) {
          onApiDeleted(gridParams?.data?.bundle?.id, RESOURCE_TYPES.BUNDLE);
        }
        sendAmplitudeData(
          AMPLITUDE_EVENTS.DELETE_BUNDLE_CONFIRMATION_CANCEL,
          orgAmplitudeProperties
        );
        targetRef.current?.focus();
      },
      [
        orgAmplitudeProperties,
        onApiDeleted,
        gridParams?.data?.bundle?.id,
      ]
    );

    const handleSelect = useCallback(
      (option) => {
        setIsOpen(false);

        const trackMenuAction = (actionLabel) => {
          if (isBundle) {
            trackManageApiIntegration({
              actionType: actionLabel,
              bundleId: gridParams?.data?.bundle?.id || gridParams?.data?.id,
              bundleDisplayName: gridParams?.data?.bundle?.name || gridParams?.data?.name,
              isBundle: true,
            });
          } else {
            trackManageApiIntegration({
              actionType: actionLabel,
              apiId: gridParams?.data?.id,
              apiDisplayName: gridParams?.data?.name,
              apiType: getFormattedApiType(gridParams?.data),
              isBundle: false,
            });
          }
        };

        switch (option.action) {
          case ACTION_TYPES.SEE_TERMS:
            trackMenuAction(option.label);
            setViewApiTerms(true);
            sendAmplitudeData(
              AMPLITUDE_EVENTS.API_MENU_VIEW_TERMS,
              orgAmplitudeProperties
            );
            break;

          case ACTION_TYPES.EDIT_PLAN:
            if (isBundle) {
              const bundleData = gridParams?.data?.bundle;
              setSelectedAPIEditPlanContext({
                name: bundleData?.name,
                id: bundleData?.id,
                type: RESOURCE_TYPES.BUNDLE,
                integrations: bundleIntegration?.integrations || [],
                pricingPlans: bundleIntegration?.pricingPlans || bundleData?.plans || [],
                consent: gridParams?.data?.consent,
              });
              setShowEditPlan(true);
            } else {
              dispatch(
                getAPIDetailsById({
                  apiId: gridParams?.data?.id,
                  apiType: gridParams?.data?.type,
                  token,
                })
              );
              setSelectedAPIEditPlanContext({
                name: gridParams?.data?.name,
                id: gridParams?.data?.id,
                type: gridParams?.data?.type,
                integrations: gridParams?.data?.integrations,
                pricingPlans: gridParams?.data?.pricingPlans,
                consent: gridParams?.data?.consent,
              });
              setShowEditPlan(true);
            }
            sendAmplitudeData(
              AMPLITUDE_EVENTS.API_MENU_EDIT_PRICING_PLAN,
              orgAmplitudeProperties
            );
            break;

          case ACTION_TYPES.MANAGE_LOCATIONS:
            trackMenuAction(option.label);
            if (isBundle && handleBundleEnableLocations) {
              handleBundleEnableLocations(gridParams?.data);
              sendAmplitudeData(
                AMPLITUDE_EVENTS.API_MENU_MANAGE_BUNDLE_LOCATIONS,
                orgAmplitudeProperties
              );
            }
            break;

          case ACTION_TYPES.ADD_LOCATIONS:
            trackMenuAction(option.label);
            handleAddLocation(gridParams?.data);
            sendAmplitudeData(
              AMPLITUDE_EVENTS.API_MENU_ADD_LOCATIONS,
              orgAmplitudeProperties
            );
            break;

          case ACTION_TYPES.REMOVE_LOCATIONS:
            trackMenuAction(option.label);
            handleRemoveLocation(gridParams?.data);
            sendAmplitudeData(
              AMPLITUDE_EVENTS.API_MENU_REMOVE_LOCATIONS,
              orgAmplitudeProperties
            );
            break;

          case ACTION_TYPES.DELETE_API:
            if (isBundle) {
              setViewDeleteBundleDialog(true);
              sendAmplitudeData(
                AMPLITUDE_EVENTS.API_MENU_DELETE_BUNDLE,
                orgAmplitudeProperties
              );
            } else {
              setViewDeleteApiDialog(true);
              sendAmplitudeData(
                AMPLITUDE_EVENTS.API_MENU_DELETE_API,
                orgAmplitudeProperties
              );
            }
            break;

          default:
            break;
        }

        targetRef.current?.focus();
      },
      [
        dispatch,
        gridParams?.data,
        handleAddLocation,
        handleRemoveLocation,
        handleBundleEnableLocations, // ADD THIS TO DEPENDENCIES
        isBundle,
        isPartOfBundle,
        orgAmplitudeProperties,
        setSelectedAPIEditPlanContext,
        setShowEditPlan,
        token,
      ]
    );

    const isOptionDisabled = useCallback(
      (option) => {
        if (isBundle) {
          if (isFetchingBundleLocations && option.label === 'Manage Locations') {
            return true;
          }
          // Disable Manage Locations and Edit Pricing Plan for bundles with pending review APIs
          if (hasPendingReviewApi && 
              (option.label === 'Manage Locations' || option.label === 'Edit Pricing Plan')) {
            return true;
          }
          return false;
        }

        const isNonViewTermsDisabled =
          !hasIntegrationCertified(gridParams?.data?.integrations) &&
          option.label !== 'View Terms' &&
          option.label !== 'Delete';

        return isNonViewTermsDisabled;
      },
      [
        isBundle,
        isFetchingBundleLocations,
        hasPendingReviewApi,
        gridParams?.data?.integrations,
      ]
    );

    const isActionButtonDisabled = isLoadingTableData && !gridParams?.data;

    return (
      <>
        <ActionMenuWrapper>
          <IconButton
            icon={<IconMoreVert />}
            text="Action"
            ref={targetRef}
            isDisabled={isActionButtonDisabled}
          />
          <Menu
            dataTestId="action-button-menu-container"
            labelRef={targetRef}
            onClose={handleClose}
            onOpen={handleOpen}
            isAuto
          >
            {options.map((option) => (
              <MenuListItem
                id={option.label}
                dataTestId={`menu-item-${option.label}`}
                key={`menu-${option.label}`}
                onClick={() => handleSelect(option)}
                graphicContent={option.icon}
                isDisabled={isOptionDisabled(option)}
              >
                {option.label}
              </MenuListItem>
            ))}
          </Menu>
        </ActionMenuWrapper>

        {viewApiTerms && (
          <APITerms
            data={gridParams?.data}
            isOpen={viewApiTerms}
            handleClose={handleCloseApiTerms}
          />
        )}

        {viewDeleteApiDialog && (
          <APIDelete
            data={gridParams?.data}
            isOpen={viewDeleteApiDialog}
            handleClose={handleCloseDeleteApiDialog}
          />
        )}

        {viewDeleteBundleDialog && (
          <APIDeleteBundle
            data={gridParams?.data?.bundle}
            isOpen={viewDeleteBundleDialog}
            handleClose={handleCloseDeleteBundleDialog}
          />
        )}
      </>
    );
  }
);
ActionMenuRenderer.displayName = 'ActionMenuRenderer';

const ApiRowContent = memo(({
  api,
  isLoadingTableData,
  isPartOfBundle = false,
  bundleId,
  bundleLocationsData,
  subscriptionCounts,
}) => (
  <>
    <StyledSimpleTableData>
      <ApiTypeButton type={api.type} isLoadingTableData={isLoadingTableData} />
    </StyledSimpleTableData>
    <StyledSimpleTableData>
      {hasIntegrationCertified(api?.integrations) ? (
        <LocationsRender
          data={api}
          isLoadingTableData={isLoadingTableData}
          isPartOfBundle={isPartOfBundle}
          bundleId={bundleId}
          bundleLocationsData={bundleLocationsData}
          subscriptionCount={subscriptionCounts?.[api?.id]}
        />
      ) : (
        <div data-testid="pending-review" className="pending-review">
          Pending CDK Review
        </div>
      )}
    </StyledSimpleTableData>
    <StyledSimpleTableData>
      <WebhookRender
        data={{ data: api }}
        isLoadingTableData={isLoadingTableData}
      />
    </StyledSimpleTableData>
  </>
));
ApiRowContent.displayName = 'ApiRowContent';

const ExpandableBundleRow = memo(
  ({
    bundle,
    bundleIntegration,
    isLoadingTableData,
    orgAmplitudeProperties,
    setShowEditPlan,
    setSelectedAPIEditPlanContext,
    handleAddLocation,
    handleRemoveLocation,
    restApis,
    asyncApis,
    consents,
    onApiDeleted,
    orders,
    token,
    bundleLocationsMap,
    isFetchingBundleLocations,
    subscriptionCounts,
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dispatch = useDispatch();

    const handleToggle = useCallback(() => setIsOpen((prev) => !prev), []);

    const isBundleContracted = Boolean(bundle?.isContracted);

    const handleBundleAddLocation = useCallback(
      async (bundleData, bundleLocationsData) => {
        const activePricingPlanId =
          bundleIntegration?.integrations?.[0]?.pricingPlanId;
        const matchingPlan = bundleIntegration?.pricingPlans?.find(
          (plan) => plan.id === activePricingPlanId
        );
        const planPrice = matchingPlan?.price || 0;
        let locationsData = bundleLocationsData;
        if (!locationsData || !Array.isArray(locationsData)) {
          try {
            const result = await dispatch(
              bundleSelectedLocations({
                entityId: bundleData.entityId || bundleData.id,
                orderId: orders?.app?.id,
                token: token,
              })
            ).unwrap();
            const rawLocations = result?.response?.data;
            const allLocations = Array.isArray(rawLocations)
              ? rawLocations
              : rawLocations?.bundleLocations;
            const bundleKey = bundleData.entityId || bundleData.id;
            locationsData = (Array.isArray(allLocations) ? allLocations : []).filter(
              (loc) => loc?.bundleId === bundleKey
            );
          } catch (error) {
            console.error('Error fetching bundle locations:', error);
            locationsData = [];
          }
        }

        dispatch(
          setScreen({
            screen: screens.DAAB_BUNDLE_ADD_LOCATIONS,
            additionalData: {
              ...bundleData,
              bundleLocations: locationsData,
              activePricingPlanId: activePricingPlanId,
              planPrice: planPrice,
              planDetails: matchingPlan,
              orderId: orders?.app?.id,
            },
          })
        );
      },
      [dispatch, bundleIntegration, orders, token]
    );

    const bundleApis = useMemo(() => {
      const allApis = [...(restApis || []), ...(asyncApis || [])];
      const consentsByApiId = consents
        ? groupBy(Object.values(consents), ({ api }) => api)
        : {};

      return (bundle.apis || [])
        .map((bundleApi) => {
          const fullApi = allApis.find(
            (api) => api.id === bundleApi.id && api.type === bundleApi.apiType
          );
          if (!fullApi) return null;
          return {
            ...createApiDataObject(fullApi, consentsByApiId),
            isNewApi: Boolean(bundleApi?.isNewApi),
          };
        })
        .filter(Boolean);
    }, [bundle.apis, restApis, asyncApis, consents]);

    const hasPendingReviewApi = useMemo(() => {
      return bundleApis.some((api) => !hasIntegrationCertified(api?.integrations));
    }, [bundleApis]);

    const hasNewApiInBundle = useMemo(
      () =>
        (bundle?.apis || []).some((bundleApi) => Boolean(bundleApi?.isNewApi)),
      [bundle?.apis],
    );

    const bundleTypeLabel = useMemo(
      () => getBundleTypeLabel(bundle?.apis),
      [bundle?.apis],
    );
    

    return (
      <>
        {/* Bundle parent row */}
        <StyledSimpleTableRow>
          <StyledSimpleTableData>
            <div className="bundle-row-name-container">
              <StyledIconButton
                icon={<IconChevronRight />}
                onClick={handleToggle}
                text={isOpen ? 'collapse' : 'expand'}
                isOpen={isOpen}
                dataTestId={`expand-bundle-${bundle.id}`}
                isDisabled={isLoadingTableData}
              />
              <span className="bundle-name-text">
                <strong className="bundle-name-truncate">{bundle.name}</strong>
                <span className="bundle-suffix"> - Bundle</span>
                {isBundleContracted && (
                  <ContractedIconWithTooltip {...CONTRACTED_ICON_TOOLTIP_PROPS} />
                )}
                {hasNewApiInBundle && (
                  <StyledIconStatusDefault
                    aria-label="New API in bundle"
                  />
                )}
              </span>
            </div>
          </StyledSimpleTableData>
          <StyledSimpleTableData className="bundle-placeholder-cell">
            <span className="bundle-placeholder-text">{bundleTypeLabel}</span>
          </StyledSimpleTableData>
          <StyledSimpleTableData>
            {hasPendingReviewApi ? (
              <div data-testid="bundle-pending-review" className="pending-review">
                Pending CDK Review
              </div>
            ) : (
              <TextButton
                className="link-renderer text-wrap"
                text="Manage Locations"
                onClick={() => {
                  handleBundleAddLocation(
                    {
                      id: bundle.id,
                      ...bundleIntegration,
                      apis: bundle?.apis || bundleIntegration?.apis || [],
                      name: bundle.name,
                    },
                    bundleLocationsMap?.[bundle.id] || []
                  );
                  sendAmplitudeData(
                    AMPLITUDE_EVENTS.API_MENU_MANAGE_BUNDLE_LOCATIONS,
                    orgAmplitudeProperties
                  );
                }}
                isDisabled={isLoadingTableData || isFetchingBundleLocations}
              />
            )}
          </StyledSimpleTableData>
          <StyledSimpleTableData className="bundle-placeholder-cell">
            <span className="bundle-placeholder-text">-</span>
          </StyledSimpleTableData>
          <StyledSimpleTableData>
            <ActionMenuRenderer
              gridParams={{ data: { bundle, consent: consents } }}
              setShowEditPlan={setShowEditPlan}
              setSelectedAPIEditPlanContext={setSelectedAPIEditPlanContext}
              handleAddLocation={handleAddLocation}
              handleRemoveLocation={handleRemoveLocation}
              handleBundleEnableLocations={() => {
                handleBundleAddLocation(
                  {
                    id: bundle.id,
                    ...bundleIntegration,
                    apis: bundle?.apis || bundleIntegration?.apis || [],
                    name: bundle.name,
                  },
                  bundleLocationsMap?.[bundle.id] || []
                );
              }}
              isFetchingBundleLocations={isFetchingBundleLocations}
              isLoadingTableData={isLoadingTableData}
              isBundle
              onApiDeleted={onApiDeleted}
              hasPendingReviewApi={hasPendingReviewApi}
              bundleIntegration={bundleIntegration}
            />
          </StyledSimpleTableData>
        </StyledSimpleTableRow>

        {/* Child API rows (visible when expanded) */}
        {isOpen &&
          bundleApis.map((api, index) => (
            <StyledSimpleTableRow
              key={`${api.id}-${index}`}
              className="child-api-row"
            >
              <StyledSimpleTableData>
                <div className={classNames('child-api-name-container', {
                  'child-api-name-container-new-api': hasNewApiInBundle,
                })}>
                  <APINameRender
                    data={api}
                    isLoadingTableData={isLoadingTableData}
                    isPartOfBundle={true}
                  />
                </div>
              </StyledSimpleTableData>
              <ApiRowContent
                api={api}
                isLoadingTableData={isLoadingTableData}
                isPartOfBundle={true}
                bundleId={bundle.id}
                bundleLocationsData={bundleLocationsMap?.[bundle.id]}
                subscriptionCounts={subscriptionCounts}
              />
              <StyledSimpleTableData>
                <ActionMenuRenderer
                  gridParams={{ data: api }}
                  setShowEditPlan={setShowEditPlan}
                  setSelectedAPIEditPlanContext={setSelectedAPIEditPlanContext}
                  handleAddLocation={(apiData) =>
                    handleAddLocation(
                      apiData,
                      bundleLocationsMap?.[bundle.id],
                      bundle.id
                    )
                  }
                  handleRemoveLocation={(apiData) =>
                    handleRemoveLocation(
                      apiData,
                      bundleLocationsMap?.[bundle.id],
                      bundle.id
                    )
                  }
                  isLoadingTableData={isLoadingTableData}
                  isPartOfBundle
                  onApiDeleted={onApiDeleted}
                  bundleId={bundle.id}
                  bundleLocationsData={bundleLocationsMap?.[bundle.id]}
                />
              </StyledSimpleTableData>
            </StyledSimpleTableRow>
          ))}
      </>
    );
  }
);
ExpandableBundleRow.displayName = 'ExpandableBundleRow';

const StandaloneAPIRow = memo(
  ({
    api,
    isLoadingTableData,
    setShowEditPlan,
    setSelectedAPIEditPlanContext,
    handleAddLocation,
    handleRemoveLocation,
    onApiDeleted,
    subscriptionCounts,
  }) => (
    <StyledSimpleTableRow>
      <StyledSimpleTableData>
        <div className="standalone-api-name-container">
          <APINameRender data={api} isLoadingTableData={isLoadingTableData} />
        </div>
      </StyledSimpleTableData>
      <ApiRowContent 
        api={api} 
        isLoadingTableData={isLoadingTableData}
        subscriptionCounts={subscriptionCounts}
      />
      <StyledSimpleTableData>
        <ActionMenuRenderer
          gridParams={{ data: api }}
          setShowEditPlan={setShowEditPlan}
          setSelectedAPIEditPlanContext={setSelectedAPIEditPlanContext}
          handleAddLocation={handleAddLocation}
          handleRemoveLocation={handleRemoveLocation}
          isLoadingTableData={isLoadingTableData}
          onApiDeleted={onApiDeleted}
        />
      </StyledSimpleTableData>
    </StyledSimpleTableRow>
  )
);
StandaloneAPIRow.displayName = 'StandaloneAPIRow';

const TableHeader = memo(() => (
  <StyledSimpleTableHead>
    <StickyHeaderRow>
      <StickySimpleTableHeader>Name</StickySimpleTableHeader>
      <StickySimpleTableHeader>Type</StickySimpleTableHeader>
      <StickySimpleTableHeader>Locations</StickySimpleTableHeader>
      <StickySimpleTableHeader>Webhook URL</StickySimpleTableHeader>
      <StickySimpleTableHeader />
    </StickyHeaderRow>
  </StyledSimpleTableHead>
));
TableHeader.displayName = 'TableHeader';

const APIIntegrationsTableV2 = ({ isLoadingTableData }) => {
  const dispatch = useDispatch();
  const {
    token,
    orders,
    orderUpdated,
    errorUpdatingOrder,
    consents,
    bundleConsents,
    subscriptionCounts,
    orgAmplitudeProperties,
  } = useAppState();

  const [tableData, setTableData] = useState([]);
  const [showEditPlan, setShowEditPlan] = useState(false);
  const [selectedAPIEditPlanContext, setSelectedAPIEditPlanContext] = useState(
    {}
  );
  const [bundleLocationsMap, setBundleLocationsMap] = useState({});
  const [isFetchingBundleLocations, setIsFetchingBundleLocations] =
    useState(false);
  const [showWebhookWarningDialog, setShowWebhookWarningDialog] = useState(false);
  const activeApiListSignatureRef = useRef('');

  const { restApis, asyncApis, bundleIntegrations } = orders || {};

  const activeBundles = useMemo(
    () => bundleIntegrations || [],
    [bundleIntegrations]
  );
  useEffect(() => {
    let isMounted = true;
    const fetchBundleLocations = async () => {
      if (!activeBundles?.length || !orders?.app?.id || !token) {
        setBundleLocationsMap((prev) =>
          isSameSerializedData(prev, {}) ? prev : {}
        );
        setIsFetchingBundleLocations(false);
        return;
      }

      setIsFetchingBundleLocations(true);
      const locationsMap = {};
      try {
        const result = await dispatch(
          bundleSelectedLocations({
            // entityId is currently unused by the API call; pass any bundle id for compatibility
            entityId: activeBundles?.[0]?.id,
            orderId: orders.app.id,
            token: token,
          })
        ).unwrap();

        const rawLocations = result?.response?.data;
        const allLocations = Array.isArray(rawLocations)
          ? rawLocations
          : rawLocations?.bundleLocations;
        const groupedByBundleId = groupBy(
          Array.isArray(allLocations) ? allLocations : [],
          (loc) => loc?.bundleId
        );

        activeBundles.forEach((bundle) => {
          locationsMap[bundle.id] = groupedByBundleId[bundle.id] || [];
        });
      } catch (error) {
        console.error('Error fetching bundle locations:', error);
        activeBundles.forEach((bundle) => {
          locationsMap[bundle.id] = [];
        });
      }

      if (isMounted) {
        setBundleLocationsMap((prev) =>
          isSameSerializedData(prev, locationsMap) ? prev : locationsMap
        );
        setIsFetchingBundleLocations(false);
      }
    };
    fetchBundleLocations();
    return () => {
      isMounted = false;
    };
  }, [activeBundles, orders?.app?.id, token, dispatch]);

  const sortedTableData = useMemo(() => {
    const bundleItems = (activeBundles || []).map((bundle) => ({
      ...bundle,
      isBundle: true,
    }));
    const apiItems = (tableData || []).map((api) => ({
      ...api,
      isBundle: false,
    }));
    return sortByName([...bundleItems, ...apiItems]);
  }, [tableData, activeBundles]);

  const tableBodyRef = useScrollToTop([
    tableData,
    activeBundles,
    isLoadingTableData,
  ]);

  useEffect(() => {
    if (isLoadingTableData) {
      return;
    }

    const hasApis = restApis?.length > 0 || asyncApis?.length > 0;
    if (!hasApis) {
      setTableData((prev) => (prev.length ? [] : prev));
      return;
    }

    const consentsByApiId = consents
      ? groupBy(Object.values(consents), ({ api }) => api)
      : {};
    const appIntegrations = [...(restApis || []), ...(asyncApis || [])];

    const apiIdsInBundles = new Set();
    bundleIntegrations?.forEach((bundleInteg) => {
      bundleInteg?.apis?.forEach((api) => {
        apiIdsInBundles.add(`${api.id}-${api.apiType}`);
      });
    });

    const standaloneApis = appIntegrations.filter(
      (api) => !apiIdsInBundles.has(`${api.id}-${api.type}`)
    );

    const data = standaloneApis.map((api) =>
      createApiDataObject(api, consentsByApiId)
    );

    setTableData((prev) => (isSameSerializedData(prev, data) ? prev : data));

    const activeApiListSignature = JSON.stringify(appIntegrations);
    if (activeApiListSignatureRef.current !== activeApiListSignature) {
      activeApiListSignatureRef.current = activeApiListSignature;
      dispatch(storeActiveApiList(appIntegrations));
    }
  }, [
    restApis,
    asyncApis,
    consents,
    isLoadingTableData,
    bundleIntegrations,
    dispatch,
  ]);

  const asyncApisRef = useRef(asyncApis);
  useEffect(() => {
    asyncApisRef.current = asyncApis;
  }, [asyncApis]);

  const handleAddLocation = useCallback(
    (apiData, bundleLocations = null, bundleId = null) => {
      if(apiData?.type === RESOURCE_TYPES.ASYNC_API && (!apiData?.integrations?.[0]?.webhookUrl || apiData?.integrations?.[0]?.webhookUrl === DUMMY_WEBHOOK_URL)) {
        setShowWebhookWarningDialog(true);
      } else {
        dispatch(
          setScreen({
            screen: screens.DAAB_ADD_LOCATIONS,
            solutionData: orders?.app,
            additionalData: {
              ...apiData,
              bundleLocations: bundleLocations,
              bundleId: bundleId,
            },
          })
        );
      }
    },
    [dispatch, orders?.app]
  );

  const handleRemoveLocation = useCallback(
    (apiData, bundleLocations = null, bundleId = null) => {
      dispatch(
        setScreen({
          screen: screens.DAAB_REMOVE_LOCATIONS,
          solutionData: orders?.app,
          additionalData: {
            ...apiData,
            bundleLocations: bundleLocations,
            bundleId: bundleId,
          },
        })
      );
    },
    [dispatch, orders?.app]
  );

  const handleEditPlanClose = useCallback(() => {
    setShowEditPlan(false);
    sendAmplitudeData(
      AMPLITUDE_EVENTS.MY_APIS_EDIT_PRICING_PLAN_CANCEL,
      orgAmplitudeProperties
    );
  }, [orgAmplitudeProperties]);

  const handleEditPlanAccept = useCallback(() => {
    setShowEditPlan(false);
  }, []);

  const handleApiDeleted = useCallback(
    (deletedApiId, deletedApiType) => {
      if (!deletedApiId) return;

      setTableData((prevData) =>
        prevData.filter((api) => api.id !== deletedApiId)
      );

      const updatedApiList = [...(restApis || []), ...(asyncApis || [])].filter(
        (api) => api.id !== deletedApiId
      );

      dispatch(storeActiveApiList(updatedApiList));
    },
    [dispatch, restApis, asyncApis]
  );

  const totalItems = activeBundles.length + tableData.length;
  const showTable = !isLoadingTableData && totalItems > 0;

  return (
    <div data-testid="root-div">
      <div
        id="ag-grid-container"
        className={classNames(
          'api-integration-table-container',
          'ag-theme-balham'
        )}
      >
        {showTable ? (
          <StyledSimpleTableWrapper>
            <StyledSimpleTableContainer>
              <StyledSimpleTable
                aria-label="API Integrations Table"
                id="api-integration-table"
              >
                <TableHeader />
                <StyledSimpleTableBody
                  ref={tableBodyRef}
                  data-testid="api-table-body"
                  key={`table-body-${sortedTableData.length}`}
                >
                  {sortedTableData.map((item) =>
                    item.isBundle ? (
                      <ExpandableBundleRow
                        key={`bundle-${item.id}`}
                        bundle={item}
                        bundleIntegration={bundleIntegrations?.find(
                          (bi) => bi.id === item.id
                        )}
                        isLoadingTableData={isLoadingTableData}
                        setShowEditPlan={setShowEditPlan}
                        setSelectedAPIEditPlanContext={
                          setSelectedAPIEditPlanContext
                        }
                        handleAddLocation={handleAddLocation}
                        handleRemoveLocation={handleRemoveLocation}
                        restApis={restApis}
                        asyncApis={asyncApis}
                        consents={bundleConsents[item.id]}
                        onApiDeleted={handleApiDeleted}
                        orders={orders}
                        token={token}
                        bundleLocationsMap={bundleLocationsMap}
                        isFetchingBundleLocations={isFetchingBundleLocations}
                        subscriptionCounts={subscriptionCounts}
                      />
                    ) : (
                      <StandaloneAPIRow
                        key={`api-${item.id}`}
                        api={item}
                        isLoadingTableData={isLoadingTableData}
                        setShowEditPlan={setShowEditPlan}
                        setSelectedAPIEditPlanContext={
                          setSelectedAPIEditPlanContext
                        }
                        handleAddLocation={handleAddLocation}
                        handleRemoveLocation={handleRemoveLocation}
                        onApiDeleted={handleApiDeleted}
                        subscriptionCounts={subscriptionCounts}
                      />
                    )
                  )}
                </StyledSimpleTableBody>
              </StyledSimpleTable>
            </StyledSimpleTableContainer>
          </StyledSimpleTableWrapper>
        ) : (
          <NoDataOverlay message="There are no APIs to show." />
        )}
      </div>

      <APIPricingPlan
        title="Edit Pricing Plan"
        apiId={selectedAPIEditPlanContext?.id}
        isOpen={showEditPlan}
        onClose={handleEditPlanClose}
        onAccept={handleEditPlanAccept}
        selectedAPIEditPlanContext={selectedAPIEditPlanContext}
        pageTitle={PAGE_TITLE.MY_APIS}
      />

      <WebhookWarningDialog
        isOpen={showWebhookWarningDialog}
        onClose={() => setShowWebhookWarningDialog(false)}
      />
    </div>
  );
};

export default APIIntegrationsTableV2;

export { WebhookRender, ActionMenuRenderer };